// Cockpit revamp — single audit-log row. Used by the right-rail "Recent
// audit" card AND the Activity tab.
//
// Entries fall into one of three buckets — `manual` (operator action,
// e.g. transition / edit), `sequence` (campaign-driven), `system`
// (background jobs). The dot-colour and label-prefix vary by bucket;
// reasoning text is rendered italicised below the headline.
import React from 'react';
import clsx from 'clsx';

import type { CampaignAuditLogEntry } from '../../../types/campaign';

export type AuditKind = 'manual' | 'sequence' | 'system';

const KIND_DOT: Record<AuditKind, string> = {
  manual: 'bg-accent',
  sequence: 'bg-info',
  system: 'bg-slate-500',
};

const KIND_LABEL: Record<AuditKind, string> = {
  manual: 'manual',
  sequence: 'sequence',
  system: 'system',
};

/** Map the backend's free-form `event_type` to a display bucket. */
export function classifyAuditEntry(
  entry: CampaignAuditLogEntry,
): AuditKind {
  if (entry.event_type === 'transition' || entry.event_type === 'force_end') {
    return 'manual';
  }
  if (entry.event_type === 'edit') {
    // Edits via PUT (cockpit inline AI-context / KPI target writes) are
    // manual; create-time `edit` rows from `create_campaign` carry
    // payload.created=true and are treated as manual too — the operator
    // created the row.
    return 'manual';
  }
  if (entry.event_type === 'enrolment_close') return 'manual';
  // Anything else (sequence-driven enrolment transitions, system jobs)
  // bucketed under `sequence` for now; future event_types can split out.
  return 'sequence';
}

/** Pull a one-line headline out of an audit entry. */
export function describeAuditEntry(entry: CampaignAuditLogEntry): React.ReactNode {
  const t = entry.event_type;
  if (t === 'transition' || t === 'force_end') {
    return (
      <>
        <strong>{verbForTransition(entry)}</strong> ·{' '}
        <span className="font-mono text-slate-400">{entry.from_status ?? '—'}</span>
        {' → '}
        <span className="font-mono text-slate-200">{entry.to_status ?? '—'}</span>
      </>
    );
  }
  if (t === 'edit') {
    const changes = (entry.payload?.['changes'] as Record<string, unknown>) ?? null;
    if (entry.payload?.['created'] === true) {
      return <em>Campaign created</em>;
    }
    if (changes && typeof changes === 'object') {
      const fields = Object.keys(changes);
      if (fields.length === 1) {
        return (
          <>
            <strong>edited</strong>{' '}
            <code className="text-slate-200">{labelForField(fields[0])}</code>
          </>
        );
      }
      return (
        <>
          <strong>edited</strong>{' '}
          <span className="text-slate-300">
            {fields.map(labelForField).join(', ')}
          </span>
        </>
      );
    }
    return <strong>edited campaign</strong>;
  }
  return <span className="capitalize">{t.replace(/_/g, ' ')}</span>;
}

function verbForTransition(entry: CampaignAuditLogEntry): string {
  if (entry.to_status === 'force_ended') return 'force-ended';
  if (entry.to_status === 'paused') return 'paused';
  if (entry.to_status === 'enrolment_closed') return 'closed enrolment';
  if (entry.to_status === 'active') {
    if (entry.from_status === 'draft') return 'launched';
    if (entry.from_status === 'paused') return 'resumed';
    return 'transitioned';
  }
  if (entry.to_status === 'completed') return 'completed';
  return 'transitioned';
}

function labelForField(field: string): string {
  if (field === 'ai_context') return 'AI context';
  if (field === 'kpis') return 'KPI targets';
  if (field === 'campaign_goals') return 'campaign goals';
  // LT-407: keep the legacy mapping for audit rows captured before the
  // rename — historic JSON payloads still have ``field: 'goal_text'``.
  if (field === 'goal_text') return 'campaign goals';
  if (field === 'enrolment_closes_at') return 'enrolment-close date';
  return field.replace(/_/g, ' ');
}

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

interface AuditRowProps {
  entry: CampaignAuditLogEntry;
  /** When true, renders compactly (right-rail card). */
  compact?: boolean;
  /** Optional trailing slot — used by the Activity tab to attach the
   * "↓ which?" expand toggle on system rows. */
  trailing?: React.ReactNode;
}

export const AuditRow: React.FC<AuditRowProps> = ({
  entry,
  compact = false,
  trailing,
}) => {
  const kind = classifyAuditEntry(entry);
  return (
    <div
      className={clsx(
        'flex items-start gap-2',
        compact ? 'py-1.5' : 'py-2',
      )}
    >
      <div
        aria-hidden="true"
        className={clsx(
          'mt-1.5 w-1.5 h-1.5 rounded-full flex-none',
          KIND_DOT[kind],
        )}
      />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-slate-300 leading-snug">
          {describeAuditEntry(entry)}
        </div>
        {entry.reason ? (
          <div className="text-[11px] text-slate-400 italic mt-0.5">
            "{entry.reason}"
          </div>
        ) : null}
        <div className="text-[10px] font-mono text-slate-500 mt-0.5 flex gap-2">
          <span className="uppercase tracking-wider">{KIND_LABEL[kind]}</span>
          <span>{formatWhen(entry.created_at)}</span>
        </div>
      </div>
      {trailing ? <div className="flex-none">{trailing}</div> : null}
    </div>
  );
};

export default AuditRow;
